import React, { useState, useRef, useEffect } from 'react';

export function FilterSelect({
  value,
  onChange,
  placeholder,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  options: { value: string; label: string }[];
}) {
  const [open, setOpen] = useState(false);
  /* Index into allOptions (placeholder = 0). -1 = no keyboard focus yet. */
  const [focusIdx, setFocusIdx] = useState(-1);
  const ref = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  /* Placeholder acts as the "clear" option at index 0. */
  const allOptions = [{ value: '', label: placeholder }, ...options];

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  /* Move DOM focus to the active option so screen readers track it. */
  useEffect(() => {
    if (open && focusIdx >= 0) {
      (listRef.current?.children[focusIdx] as HTMLElement | undefined)?.focus();
    }
  }, [open, focusIdx]);

  const close = (refocus = false) => {
    setOpen(false);
    setFocusIdx(-1);
    if (refocus) btnRef.current?.focus();
  };

  const select = (v: string) => {
    onChange(v);
    close(true);
  };

  const handleButtonKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setFocusIdx(0);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      setFocusIdx(allOptions.length - 1);
    } else if (e.key === 'Escape' && open) {
      e.preventDefault();
      close();
    }
  };

  const handleListKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusIdx(i => Math.min(allOptions.length - 1, i + 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusIdx(i => Math.max(0, i - 1));
        break;
      case 'Home':
        e.preventDefault();
        setFocusIdx(0);
        break;
      case 'End':
        e.preventDefault();
        setFocusIdx(allOptions.length - 1);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (focusIdx >= 0) select(allOptions[focusIdx].value);
        break;
      case 'Escape':
        e.preventDefault();
        close(true);
        break;
      case 'Tab':
        close();
        break;
    }
  };

  const selected = options.find(o => o.value === value);

  return (
    <div className="fsn-select" ref={ref}>
      <button
        type="button"
        ref={btnRef}
        className={`fsn-select__btn${value ? ' fsn-select__btn--active' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
        onKeyDown={handleButtonKeyDown}
      >
        <span className="fsn-select__label">{selected ? selected.label : placeholder}</span>
        <span className="fsn-select__arrow" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div
          className="fsn-select__dropdown"
          role="listbox"
          aria-label={placeholder}
          ref={listRef}
          onKeyDown={handleListKeyDown}
        >
          {allOptions.map((o, i) => (
            <div
              key={o.value || '__all__'}
              role="option"
              aria-selected={value === o.value || (!value && o.value === '')}
              tabIndex={-1}
              className={`fsn-select__option${(value === o.value || (!value && o.value === '')) ? ' fsn-select__option--sel' : ''}`}
              onClick={() => select(o.value)}
              onMouseMove={() => focusIdx !== i && setFocusIdx(i)}
            >{o.label}</div>
          ))}
        </div>
      )}
    </div>
  );
}
